import { entryFactory, pointOfServiceFactory, priceFactory } from 'factories';
import { convertTimestampToDate } from 'helpers';
import {
  Address,
  ConsignmentDetails,
  Employee,
  Entry,
  PointOfSale,
  PointOfSaleType,
  Voucher,
} from 'types';

function populateEmployeeData(employeeData: any): Employee {
  return {
    email: employeeData?.email || '',
    name: employeeData?.name || '',
    cpf: employeeData?.cpf || '',
    groups: {
      brandGroups: employeeData?.brandGroups || [''],
      roleGroups: employeeData?.roleGroups || [''],
      storeGroups: employeeData?.storeGroups || [''],
    },
  };
}

function populateAddressData(addressData: any = {}): Address {
  const {
    phone,
    phone2,
    town,
    streetName,
    streetNumber,
    complement,
    district,
    postalCode,
    receiverFirstName,
    receiverLastName,
    region,
    country,
  } = addressData;

  return {
    phone1: phone || '',
    phone2: phone2 || '',
    town: town || '',
    streetName: streetName || '',
    streetNumber: streetNumber || '',
    complement: complement || '',
    district: district || '',
    postalCode: postalCode || '',
    receiverFirstName: receiverFirstName || '',
    receiverLastName: receiverLastName || '',
    region: {
      isoCode: region?.isocode || '',
      name: region?.name || '',
    },
    country: {
      isoCode: country?.isocode || '',
      name: country?.name || '',
    },
  };
}

function populateEntriesData(entriesData: any = []): Entry[] {
  return entriesData.map((entry: any) => {
    return entryFactory(entry);
  });
}

function populateVouchersData(vouchersData: any): Voucher[] {
  const vouchers = vouchersData || [];

  return vouchers.map((voucher: any) => voucher || { code: '', name: '' });
}

export function consignmentDetailsFactory(
  params: any = {}
): ConsignmentDetails {
  const {
    lastRegistration,
    customer,
    reservedPOS,
    assignedPOS,
    reservedEmployee,
    assignedEmployee,
    deliveryMode,
    shippingAddress,
    payment,
    entries,
    distance,
    pointOfSale,
  } = params || {};

  return {
    code: params?.code || '',
    orderCode: params?.orderCode || '',
    createdDate: convertTimestampToDate(params?.createdDate || 0),
    baseStore: params?.baseStore?.toLowerCase() || '',
    status: params?.status || '',
    salesMode: params?.salesMode || '',
    daysToDelivery: params?.daysToDelivery || 0,
    quotationCode: params?.quotationCode || '',
    quotationId: params?.quotationId || '',
    nfeKey: params?.nfeKey || '',
    nfeNumber: params?.nfeNumber || '',
    nfeSerie: params?.nfeSerie || '',
    nfeLink: params?.nfeLink || '',
    reservedPOS: pointOfServiceFactory(reservedPOS),
    reservedEmployee: populateEmployeeData(reservedEmployee),
    assignedPOS: pointOfServiceFactory(assignedPOS),
    assignedEmployee: populateEmployeeData(assignedEmployee),
    customer: {
      firstName: customer?.firstName || '',
      lastName: customer?.lastName || '',
      cpf: customer?.cpf || '',
      phone: customer?.phone || '',
      cellphone: customer?.cellphone || '',
      email: customer?.email || '',
    },
    deliveryMode: {
      code: deliveryMode?.code || '',
      name: deliveryMode?.name || '',
      tmsId: deliveryMode?.tmsId || '',
      description: deliveryMode?.description || '',
      salesMode: deliveryMode?.salesMode || '',
    },
    payment: {
      status: payment?.status || '',
      costOfDeliveryPrice: priceFactory(payment?.costOfDeliveryPrice),
      subTotalPrice: priceFactory(payment?.subTotalPrice),
      discountsPrice: priceFactory(payment?.discountsPrice),
      totalPrice: priceFactory(payment?.totalPrice),
      installments: payment?.installments || 0,
      installmentsValuePrice: priceFactory(payment?.installmentsValuePrice),
      installmentsValueWithoutDeliveryPrice: priceFactory(
        payment?.installmentsValueWithoutDeliveryPrice
      ),
      transactionId: payment?.transactionId || '',
      paymentProvider: payment?.paymentProvider || '',
      paymentType: payment?.paymentType || '',
      paymentInfo: {
        accountHolderName: payment?.paymentInfo?.accountHolderName || '',
        cardNumber: payment?.paymentInfo?.cardNumber || '',
        cardType: payment?.paymentInfo?.cardType || '',
      },
    },
    entries: populateEntriesData(entries),
    shippingAddress: populateAddressData(shippingAddress),
    lastRegistration: {
      carrier: lastRegistration?.carrier || '',
      date: convertTimestampToDate(lastRegistration?.date || 0),
      receiverCpf: lastRegistration?.receiverCpf || '',
      receiverName: lastRegistration?.receiverName || '',
    },
    observationOfConsignment: params?.observationOfConsignment || '',
    vouchers: populateVouchersData(params?.vouchers),
    endDate: convertTimestampToDate(params?.endDate || 0),
    startDate: convertTimestampToDate(params?.startDate || 0),
    distance: distance || 0,
    pointOfSale: {
      isInvoiceGenerator: pointOfSale?.isInvoiceGenerator || false,
      isTaxCouponGenerator: pointOfSale?.isTaxCouponGenerator || false,
      name: pointOfSale?.name || PointOfSale.Manual,
      type: pointOfSale?.type || PointOfSaleType.Manual,
    },
    logisticsFailureReason: params?.logisticsFailureReason || undefined,
    isSuperExpress: params?.isSuperExpress || false,
    pinCodes: {
      superExpressDropoffCode: params?.pinCodes?.superExpressDropoffCode || '',
      superExpressPickupCode: params?.pinCodes?.superExpressPickupCode || '',
      superExpressReturnCode: params?.pinCodes?.superExpressReturnCode || '',
    },
    scheduledCollect: {
      driverName: params?.scheduledCollect?.driverName || '',
    },
  };
}
